import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FileText, FileSpreadsheet, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AssistantContext } from "@/contexts/AssistantContext";
import { useReportPdf } from "@/components/reports/useReportPdf";
import { filasDelInforme, type AssistantDocSpec } from "@/hooks/useAssistantStream";
import { buildReportXlsx, deliverXlsx, xlsxFilename } from "@/lib/reportXlsx";
import { cargarMarcaInforme, claveCabecera } from "@/lib/marca/cabeceraInforme";
import { esCampoCabecera } from "@/lib/marca/reglasCabecera";
import { buildActionUrl, getAssistantAction, isAllowedAssistantUrl } from "@/lib/assistantActions";
import { prefijoFichero } from "@/lib/marca/fichero";

/** Formatos que enseña la tarjeta: los que manda el backend, o los dos si no dice. */
function docFormats(doc: AssistantDocSpec): Array<"pdf" | "xlsx"> {
  const f = (doc.formats ?? []).filter((x) => x === "pdf" || x === "xlsx");
  return f.length > 0 ? f : ["pdf", "xlsx"];
}

const PDF_COLOR = "#E5574E";
const XLSX_COLOR = "#1D7A46";
const BOTH_COLOR = "#2E84DE";

/**
 * «Falta NIF · Añadir» (F4 de «Personalizar informes»): un chip por cada dato de la
 * cabecera que el informe pidió y la empresa aún no tiene guardado. Lleva a
 * Ajustes → Personalizar informes → Datos de la cabecera (solo el admin: la página
 * es suya). El informe ya se puede descargar sin ellos.
 */
function ChipsCabecera({ faltan }: { faltan: string[] }) {
  const { t } = useTranslation();
  const { role } = useAuth();
  const navigate = useNavigate();
  const assistant = useContext(AssistantContext);
  const accion = getAssistantAction("settings_marca");
  const url = role === "admin" && accion ? buildActionUrl(accion, { values: { mode: "cabecera" } }) : null;
  const ir = () => {
    if (!url || !isAllowedAssistantUrl(url)) return;
    assistant?.setIsPanelOpen(false);
    navigate(url);
  };
  return (
    <div data-testid="assistant-report-header-missing" className="mt-2 flex flex-wrap gap-1.5">
      {faltan.map((c) => {
        const texto = t("marcaInformes.asistente.falta", { campo: t(`marcaInformes.cabecera.campos.${c}`) });
        return url ? (
          <button
            key={c}
            type="button"
            onClick={ir}
            data-testid="assistant-report-header-add"
            className="inline-flex min-h-[32px] items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 text-[12px] font-semibold text-amber-900 transition-all active:scale-[0.98]"
          >
            <span>{texto}</span>
            <span aria-hidden>·</span>
            <Plus className="h-3.5 w-3.5" />
            <span>{t("marcaInformes.asistente.anadir")}</span>
          </button>
        ) : (
          <span key={c} className="inline-flex min-h-[32px] items-center rounded-full border border-amber-300 bg-amber-50 px-2.5 text-[12px] font-semibold text-amber-900">
            {texto}
          </span>
        );
      })}
    </div>
  );
}

/**
 * El informe que ha preparado el asistente, como documento en el chat, con un
 * botón por formato: PDF y Excel, o solo el que pidió el usuario.
 *
 * PDF: abre la MISMA ventana de exportación que en /reports (vista previa,
 * descargar, compartir) y el PDF se arma con el mismo constructor, así que sale
 * idéntico a los informes de la casa. Apaisado cuando la tabla trae muchas
 * columnas; eso lo decide el backend al montar la especificación.
 *
 * Excel: el .xlsx con las mismas filas (lib/reportXlsx), directo al visor del
 * sistema en la app nativa o como descarga en el navegador.
 *
 * El informe se guarda entero con la conversación: al volver a ella la tarjeta
 * sigue aquí y descarga lo mismo. Los guardados antes sin filas salen como
 * «pídemelo otra vez».
 *
 * Cabecera (F4 de «Personalizar informes», 29-09-2026): si el informe pidió datos
 * de la empresa en la cabecera (`doc.cabecera`, solo NOMBRES), el PDF y el Excel
 * los llevan con los valores guardados (lib/marca/cabeceraInforme), y lo pedido que
 * falta sale como chip «Falta NIF · Añadir».
 */
export function AssistantReportCard({ doc }: { doc: AssistantDocSpec }) {
  const { t } = useTranslation();
  const { company } = useAuth();
  const report = useReportPdf();
  const [excelBusy, setExcelBusy] = useState(false);

  const formats = docFormats(doc);
  const onlyXlsx = formats.length === 1 && formats[0] === "xlsx";
  const onlyPdf = formats.length === 1 && formats[0] === "pdf";
  const iconColor = doc.unavailable ? "#94A3B8" : onlyXlsx ? XLSX_COLOR : onlyPdf ? PDF_COLOR : BOTH_COLOR;
  const Icon = onlyXlsx ? FileSpreadsheet : FileText;
  const rowCount = doc.unavailable ? doc.row_count : filasDelInforme(doc);
  const faltan = (doc.cabecera?.faltan ?? []).filter(esCampoCabecera);

  const openPdf = () => {
    void report.run({
      reportId: doc.report_id,
      reportTitle: doc.title,
      orientation: doc.orientation ?? "portrait",
      dateStart: doc.date_start,
      dateEnd: doc.date_end,
      filters: doc.filters,
      kpis: doc.kpis,
      columns: doc.columns,
      rows: doc.rows,
      sections: doc.sections,
      detailsLine: String(filasDelInforme(doc)),
      cabecera: doc.cabecera ?? null,
      // Otra cabecera, otro PDF (la marca ya lo lleva en su versión; esto por si viene sin marca).
      cacheExtra: doc.cabecera ? { cab: claveCabecera(doc.cabecera) } : undefined,
    });
  };

  const downloadExcel = async () => {
    if (excelBusy) return;
    setExcelBusy(true);
    try {
      const marca = await cargarMarcaInforme(company?.id, doc.cabecera);
      const blob = await buildReportXlsx(doc, company?.name ?? "", marca);
      await deliverXlsx(blob, xlsxFilename(doc, prefijoFichero(marca)));
    } catch (e) {
      console.error("[AssistantReportCard] Excel failed:", e);
      toast.error(t("ipmAssistant.reportExcelError", "No se pudo preparar el Excel. Inténtalo otra vez."));
    } finally {
      setExcelBusy(false);
    }
  };

  const pill =
    "flex-1 min-h-[38px] inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 text-[13px] font-semibold transition-all active:scale-[0.98] disabled:opacity-60";

  return (
    <>
      <div data-testid="assistant-report-card" className="mt-2 w-full rounded-2xl border border-[#b6dded] bg-white px-3 py-2.5 shadow-sm">
        <div className="flex items-center gap-3">
          <span
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{
              backgroundColor: iconColor,
              boxShadow: `0 6px 14px ${iconColor}42, inset 0 1px 0 rgba(255,255,255,0.28)`,
            }}
          >
            <Icon className="w-5 h-5 text-white" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-foreground truncate">{doc.title}</span>
            <span data-testid="assistant-report-meta" className="block text-[11px] text-muted-foreground truncate">
              {doc.unavailable
                ? t("ipmAssistant.reportUnavailable", "Ya no se puede descargar: pídemelo otra vez")
                : t("ipmAssistant.reportRows", "{{count}} filas", { count: rowCount ?? 0 })}
            </span>
          </span>
        </div>
        {!doc.unavailable && (
          <div className="mt-2.5 flex gap-2">
            {formats.includes("pdf") && (
              <button
                type="button"
                onClick={openPdf}
                aria-label={t("ipmAssistant.reportDownloadAs", "Descargar en {{format}}", { format: "PDF" })}
                className={pill}
                style={{ borderColor: `${PDF_COLOR}55`, backgroundColor: `${PDF_COLOR}0F`, color: "#B93C34" }}
              >
                <FileText className="w-4 h-4" />
                PDF
              </button>
            )}
            {formats.includes("xlsx") && (
              <button
                type="button"
                onClick={downloadExcel}
                disabled={excelBusy}
                aria-busy={excelBusy}
                aria-label={t("ipmAssistant.reportDownloadAs", "Descargar en {{format}}", { format: "Excel" })}
                className={pill}
                style={{ borderColor: `${XLSX_COLOR}55`, backgroundColor: `${XLSX_COLOR}0F`, color: XLSX_COLOR }}
              >
                {excelBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                Excel
              </button>
            )}
          </div>
        )}
        {!doc.unavailable && faltan.length > 0 && <ChipsCabecera faltan={faltan} />}
      </div>
      {report.modal}
    </>
  );
}
